// sanguine-dedicated-cast.ts — client UI for the three DEDICATED-CAST sanguines (the ones that don't
// self-buff: they open a fold-out form instead). Phase 3 of the sanguine sweep.
//
//   Ad-lib 4D7142B0 / Voidgrasping 052C008A — native class-pool choice, targeting and cast.
//   Contingency DC9778C6 — pick a stored spell + a trigger, then ARM the delayed cast (self, no target).
//
// The action-bar button for these three routes its click to ftSanguineDedicatedCast() (see
// ability-button.ts) instead of the normal self-buff use-ability path. This module fetches the caster's
// Contingency options from the held caster and posts the browser's arming plan.
//
// Deps are runtime globals (getSidelogContext / openSideLog / BASE_URL / displayWarning / checkIfValidUUID),
// resolved the same fail-soft way the rest of the PC-sheet bundle does.

import { saveDiceTruth, publishSavedDice } from '../shared/dice-publication';
import { diceAppliedFrame } from '../janus-engine/transport/dice-applied-frame';
import { encounterField } from '../shared/array-types';
import type { GmBoundaryVerdict } from '../janus-engine/statuses/gm-boundary';
import { SANGUINE_POOL_ID, SANGUINE_SCHEMA } from '../janus-engine/sanguine/sanguine-gate';
import { setAbilityCharges, type ChargeReaders } from '../janus-engine/charges/charge-adjust';
import { ensureActorRowHeld } from '../shared/actor-row-loader';
import { ftCurrentTruthSink } from '../shared/truth-sink';
import { contingencyArmingCost, contingencyEligibleSpells, contingencyKnownSpellIds, planContingencyArm, CONTINGENCY_CONDITIONS, type ContingencyReaders, type PlannedContingency } from '../janus-engine/sanguine/contingency-arm';
import { ftClockColsFromArr, ftClockCalendarFromArr, ftClockCampaignField, ftCampaignTimeAddSeconds } from '../shared/campaign-clock';
import { itemField, record } from '../janus-engine/ability/context';
import { statusRows, statusItemId } from '../janus-engine/statuses/expiry';
import { refuseUnported } from '../janus-engine/unported.js';
import { extraordinaryPool, extraordinaryChoices, extraordinaryQuote, extraordinaryAuthorize,
    type ExtraordinaryMode, type ExtraordinaryTargets, type ExtraordinaryReaders, type ExtraordinaryCast } from '../janus-engine/sanguine/extraordinary-plan-21';
import type { PoolPayload } from '../janus-engine/spells/spell-prep-table';
import type { PrepInputs } from '../janus-engine/spells/spell-prep-inputs';

const SANGUINE_ADLIB_ID       = '4D7142B0-96DE-3DAB-14CE-DBA40FAF47E8';
const SANGUINE_VOIDGRASP_ID   = '052C008A-1E70-D828-50A5-50192772B8C4';
const SANGUINE_CONTINGENCY_ID = 'DC9778C6-5B9B-1501-B86A-5EEF4671F66A';
/** The level-6 arcane SPELL "Spell Contingency" — the second door into the same arm dialog. It is NOT a
 *  sanguine (Bards and Silver Dragons cast it with no Sanguine pool at all), so it gets its own mode and
 *  its own price: its own mana rather than 5 Sanguine. Both are authorized and planned in the browser. */
const SPELL_CONTINGENCY_ID    = '3BDE1AA4-23D6-C4E8-CADD-ED15B4403EA6';

/** The dedicated-cast abilities and their mode; empty string when the id isn't one of them. */
function dedicatedSanguineMode(abilityId: string): '' | 'adlib' | 'voidgrasp' | 'contingency' {
    const id = String(abilityId ?? '').toUpperCase();
    if (id === SANGUINE_ADLIB_ID) return 'adlib';
    if (id === SANGUINE_VOIDGRASP_ID) return 'voidgrasp';
    if (id === SANGUINE_CONTINGENCY_ID || id === SPELL_CONTINGENCY_ID) return 'contingency';
    return '';
}

/** Which arming item a click came through. */
function contingencyArmingItemId(abilityId: string): string {
    return String(abilityId ?? '').toUpperCase() === SPELL_CONTINGENCY_ID
        ? SPELL_CONTINGENCY_ID
        : SANGUINE_CONTINGENCY_ID;
}

/** True for any of them — used by the button renderer to swap its click snippet. */
export function isDedicatedCastSanguine(abilityId: string): boolean {
    return dedicatedSanguineMode(abilityId) !== '';
}

interface SidelogContext {
    roll_private?: unknown;
    campaign_id?: unknown;
    encounter_id?: unknown;
    caster_id?: unknown;
    target_id?: unknown;
    raw_dice_mod?: unknown;
    raw_dice_adv_dis_stacks?: unknown;
}

function readSidelogContext(): SidelogContext | null {
    const g = (window as unknown as { getSidelogContext?: () => unknown }).getSidelogContext;
    if (typeof g !== 'function') return null;
    try {
        const sc = g();
        return sc && typeof sc === 'object' ? sc as SidelogContext : null;
    } catch {
        return null;
    }
}

function warn(message: string): void {
    const dw = (window as unknown as { displayWarning?: (m: string) => void }).displayWarning;
    if (typeof dw === 'function') dw(message);
    else console.warn(message);
}

function baseUrl(): string {
    const b = (window as unknown as { BASE_URL?: unknown }).BASE_URL;
    return typeof b === 'string' ? b : '/';
}


// ── Lane ROLLS S4 (2026-09-27): the page's caster row for the browser gates ──────────────
const gateReaders: ChargeReaders = {
    entries: (row, key) => {
        const fn = (window as unknown as { ftLayerEntries?: (r: unknown, k: string) => unknown[] | null }).ftLayerEntries;
        return row && typeof fn === 'function' ? (fn(row, key) ?? []) : [];
    },
    value: (row, key) => {
        const fn = (window as unknown as { ftLayerValue?: (r: unknown, k: string) => unknown }).ftLayerValue;
        return row && typeof fn === 'function' ? fn(row, key) : null;
    },
    /** A catalog definition the page holds (object cache, then any cached encounter's / caster's interned catalog), else null. */
    definition: (id) => {
        const key = String(id ?? '').toUpperCase();
        const ft = (window as unknown as { FateTide?: { objectCache?: Map<string, unknown>; encounterCache?: Map<string, unknown>; casterCache?: Map<string, unknown> } }).FateTide;
        const rec = (v: unknown): Record<string, unknown> | null => v && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : null;
        const hosts: unknown[] = [ft?.objectCache?.get(key)];
        for (const enc of ft?.encounterCache?.values?.() ?? []) {
            hosts.push(rec(rec(enc)?.catalog)?.[key]);
            for (const m of (rec(enc)?.encounter_creatures_arr as unknown[] | undefined) ?? []) hosts.push(rec(rec(m)?.catalog)?.[key]);
        }
        for (const c of ft?.casterCache?.values?.() ?? []) hosts.push(rec(rec(c)?.catalog)?.[key]);
        for (const h of hosts) { const d = rec(h); if (d && rec(d.items)) return d; }
        return null;
    },
};
export async function contingencyReaders(encounterId: string, displayedCampaignId = ''): Promise<ContingencyReaders> {
    const w = window as unknown as { FateTide?: { encounterCache?: Map<string, unknown>; campaignCache?: Map<string, unknown> };
        ftEncounterField?: (row: unknown, key: string) => unknown; getCampaignArr?: (id: string) => Promise<unknown>;
        ftCampaignClock?: { getDerived?: () => { cols: ReturnType<typeof ftClockColsFromArr> } | null } };
    const encounter = w.FateTide?.encounterCache?.get(encounterId.toUpperCase());
    const campaignId = String(w.ftEncounterField?.(encounter, 'encounter_master_parent_id') ?? '').toUpperCase();
    const matches = (candidate: unknown): boolean => !!campaignId && String(ftClockCampaignField(candidate, 'campaign_id') ?? '').toUpperCase() === campaignId;
    let candidate = w.FateTide?.campaignCache?.get(campaignId);
    if (!matches(candidate) && campaignId) {
        try { candidate = await w.getCampaignArr?.(campaignId); } catch { /* Non-campaign parents have no campaign clock. */ }
    }
    const campaign = matches(candidate) ? record(candidate) : null;
    const calendar = ftClockCalendarFromArr(campaign);
    return { ...gateReaders, definition: id => gateReaders.definition?.(id) ?? null,
        has: (row, key) => gateReaders.value(row, key) != null || gateReaders.entries(row, key).length > 0,
        campaignClock: () => {
            if (!campaign) return null;
            const derived = displayedCampaignId.toUpperCase() === campaignId ? w.ftCampaignClock?.getDerived?.() : null;
            return derived?.cols ?? ftClockColsFromArr(campaign);
        },
        addCampaignSeconds: (clock, seconds) => ftCampaignTimeAddSeconds(clock, calendar, seconds),
        uuid: () => crypto.randomUUID().toUpperCase() };
}
/** Install the saved values a stale answer carries on every row the page holds for the caster (Codex review F10). */
function reconcileCaster(casterId: string, encounterId: string, values: { mana?: unknown; sanguine?: unknown } | undefined): void {
    if (!values) return;
    const id = casterId.toUpperCase();
    const ft = (window as unknown as { FateTide?: { casterCache?: Map<string, unknown> } }).FateTide;
    const rows: Record<string, unknown>[] = [];
    for (const [k, v] of ft?.casterCache?.entries?.() ?? []) if (String(k).toUpperCase() === id && v && typeof v === 'object') rows.push(v as Record<string, unknown>);
    // The encounter member row is resolved on its own — it may be a DIFFERENT object than the caster-cache copy (Codex verification 10).
    const enc = (ft as { encounterCache?: Map<string, unknown> } | undefined)?.encounterCache?.get?.(encounterId)
        ?? (ft as { encounterCache?: Map<string, unknown> } | undefined)?.encounterCache?.get?.(encounterId.toUpperCase());
    for (const r of ((enc as { encounter_creatures_arr?: unknown[] } | undefined)?.encounter_creatures_arr ?? [])) {
        const ecId = (r as { encounter_creatures?: { encounter_creature_id?: unknown } } | null)?.encounter_creatures?.encounter_creature_id;
        if (String(ecId ?? '').toUpperCase() === id && !rows.includes(r as Record<string, unknown>)) rows.push(r as Record<string, unknown>);
    }
    const render = (window as unknown as { ftRenderCasterRowLocal?: (row: unknown) => void }).ftRenderCasterRowLocal;
    for (const row of rows) {
        const ec = (row.encounter_creatures ?? (row.encounter_creatures = {})) as Record<string, unknown>;
        if (typeof values.mana === 'number' && Number.isInteger(values.mana)) ec.mana = values.mana;
        if (typeof values.sanguine === 'number' && Number.isInteger(values.sanguine)) setAbilityCharges(row, SANGUINE_POOL_ID, values.sanguine, gateReaders);
        if (typeof render === 'function') render(row);
    }
}
/** The caster row this page holds (caster cache, then active encounter membership). */
function casterRowFor(casterId: string, encounterId: string): Record<string, unknown> | null {
    const id = casterId.toUpperCase();
    const ft = (window as unknown as { FateTide?: { casterCache?: Map<string, unknown>; encounterCache?: Map<string, unknown> } }).FateTide;
    for (const [k, v] of ft?.casterCache?.entries?.() ?? []) if (String(k).toUpperCase() === id && v && typeof v === 'object') return v as Record<string, unknown>;
    const enc = ft?.encounterCache?.get?.(encounterId) ?? ft?.encounterCache?.get?.(encounterId.toUpperCase());
    const rows = (enc as { encounter_creatures_arr?: unknown[] } | undefined)?.encounter_creatures_arr ?? [];
    for (const r of rows) {
        const ec = (r as { encounter_creatures?: { encounter_creature_id?: unknown } } | null)?.encounter_creatures;
        if (String(ec?.encounter_creature_id ?? '').toUpperCase() === id) return r as Record<string, unknown>;
    }
    return null;
}

// ── fold-out card ─────────────────────────────────────────────────────────────
// A single reusable floating card (Bootstrap card + a light backdrop). Rebuilt per open.

let cardEl: HTMLDivElement | null = null;
let backdropEl: HTMLDivElement | null = null;
let statusEl: HTMLDivElement | null = null;
// Bumped on every open — a slow request that resolves after its card was closed (and possibly a NEW
// card opened) must not act on the current card. The confirm handler captures the token and no-ops when
// it no longer matches (Codex review 2026-07-23 #6).
let openToken = 0;

function closeCard(): void {
    // Tear down Tom Select BEFORE the card leaves the DOM — its menu is body-mounted (see labelledSelect),
    // so dropping the card alone would orphan a dropdown on <body>.
    if (cardEl) {
        const destroyTomSelectIn = (window as unknown as { destroyTomSelectIn?: (root: unknown) => void }).destroyTomSelectIn;
        if (typeof destroyTomSelectIn === 'function') {
            try { destroyTomSelectIn(cardEl); } catch { /* leave the native select; the card is going away anyway */ }
        }
    }
    cardEl?.remove();
    backdropEl?.remove();
    cardEl = null;
    backdropEl = null;
    statusEl = null;
    document.removeEventListener('keydown', onEscKey);
}

function onEscKey(e: KeyboardEvent): void {
    if (e.key !== 'Escape') return;
    // An open Tom Select menu eats the first Escape (dismiss the search list); only the second tears down
    // the card. Without this, escaping out of a mistyped search would cancel the whole cast.
    if (cardEl?.querySelector('.ts-wrapper.dropdown-active')) return;
    closeCard();
}

/** Show a refusal / error line inside the card (textContent — never innerHTML; server text is untrusted). */
function showCardStatus(text: string): void {
    if (statusEl) statusEl.textContent = text;
}

function openCard(titleText: string): { body: HTMLDivElement; footer: HTMLDivElement } {
    closeCard();
    openToken += 1;

    backdropEl = document.createElement('div');
    backdropEl.className = 'ft-sanguine-cast-backdrop';
    Object.assign(backdropEl.style, {
        position: 'fixed', inset: '0', zIndex: '20000', background: 'rgba(0,0,0,0.45)',
    } as CSSStyleDeclaration);
    backdropEl.addEventListener('click', closeCard);

    cardEl = document.createElement('div');
    cardEl.className = 'card shadow-lg ft-sanguine-cast-card';
    Object.assign(cardEl.style, {
        position: 'fixed', zIndex: '20001', top: '10vh', left: '50%',
        transform: 'translateX(-50%)', width: 'min(92vw, 460px)', maxHeight: '86vh',
        overflow: 'auto',
    } as CSSStyleDeclaration);
    cardEl.addEventListener('click', (e) => e.stopPropagation());

    const header = document.createElement('div');
    header.className = 'card-header d-flex align-items-center justify-content-between';
    const title = document.createElement('strong');
    title.textContent = titleText;
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'btn-close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.addEventListener('click', closeCard);
    header.append(title, closeBtn);

    const body = document.createElement('div');
    body.className = 'card-body';

    statusEl = document.createElement('div');
    statusEl.className = 'px-3 small text-warning'; // refusal / error line (textContent only)
    statusEl.setAttribute('role', 'alert');

    const footer = document.createElement('div');
    footer.className = 'card-footer d-flex justify-content-end gap-2';

    cardEl.append(header, body, statusEl, footer);
    document.body.append(backdropEl, cardEl);
    document.addEventListener('keydown', onEscKey);

    return { body, footer };
}

function labelledSelect(labelText: string, placeholder: string): { wrap: HTMLDivElement; select: HTMLSelectElement } {
    const wrap = document.createElement('div');
    wrap.className = 'mb-3';
    const label = document.createElement('label');
    label.className = 'form-label small fw-semibold';
    label.textContent = labelText;
    const select = document.createElement('select');
    // ts-select = the site-wide Tom Select marker; hydrateCardSelects() skins it so a long spell list is
    // type-to-search instead of a scroll hunt. Static options only (no data-item-type) → no AJAX path.
    // The menu mounts on <body>: this card is position:fixed with overflow:auto and would clip it.
    select.className = 'ts-select';
    select.dataset.tsDropdownParent = 'body';
    select.dataset.showIcons = 'false';
    select.setAttribute('placeholder', placeholder);
    wrap.append(label, select);
    return { wrap, select };
}

function addOption(select: HTMLSelectElement, value: string, text: string): void {
    const opt = document.createElement('option');
    opt.value = value;
    opt.textContent = text; // textContent — never innerHTML for server-supplied names
    select.append(opt);
}

/** Skin every ts-select inside the open card with Tom Select (search + keyboard nav). Fail-soft: if the
 *  global initializer or the CDN library is missing, the untouched native <select> still works. Must run
 *  AFTER the selects are appended to the document — initializeTomSelect() scans from `document`. */
function hydrateCardSelects(): void {
    const initializeTomSelect = (window as unknown as { initializeTomSelect?: () => void }).initializeTomSelect;
    if (typeof initializeTomSelect !== 'function') return;
    try { initializeTomSelect(); } catch { /* native select remains usable */ }
}

/**
 * Read the user's pick correctly under Tom Select's shared single-select UX, which needs BOTH sources:
 *
 *  - Menu OPEN: onDropdownOpen stashed the value and cleared it silently (no change event) so the full
 *    list shows, so `select.value` is transiently '' — use the last value a change event reported.
 *  - Menu CLOSED: `select.value` is authoritative and is the ONLY thing that reflects a keyboard clear.
 *    Backspace/Delete on the emptied control sets _ftUserCleared, which makes onDropdownClose skip the
 *    restore WITHOUT firing change — so the tracked value would still hold the abandoned pick and we'd
 *    cast a spell the user just cleared. (The '×' clear_button calls clear() non-silently, so that path
 *    fires change and either source agrees.)
 *
 * With no Tom Select at all (fail-soft path) there is no menu, so `select.value` is simply the truth.
 */
function trackedValue(select: HTMLSelectElement): () => string {
    let lastChanged = select.value;
    select.addEventListener('change', () => { lastChanged = select.value; });
    return () => {
        const ts = (select as unknown as { tomselect?: { isOpen?: boolean } }).tomselect;
        return ts?.isOpen ? lastChanged : select.value;
    };
}

interface SpellRow {
    id: string;
    name: string;
    level: number;
    max_cast_level?: number;
}
function bodyMessage(body: HTMLDivElement, text: string, cls = 'text-muted'): void {
    body.innerHTML = '';
    const p = document.createElement('p');
    p.className = `small ${cls} mb-0`;
    p.textContent = text;
    body.append(p);
}

const extraordinaryReaders: ExtraordinaryReaders = { ...gateReaders, definition: id => gateReaders.definition?.(id) ?? null };

/** Choice → cast adapter. The websocket owner rechecks the current actor and settles everything in one transaction. */
export async function castExtraordinaryChoice(sc: SidelogContext, mode: ExtraordinaryMode, pool: PoolPayload,
    spellId: string, summonPick: string, targets: ExtraordinaryTargets): Promise<ActionResult> {
    const casterId = String(sc.caster_id ?? ''), encounterId = String(sc.encounter_id ?? '');
    const caster = casterRowFor(casterId, encounterId);
    if (!caster) return { ok: false, reason: 'The caster is unavailable.' };
    const extraordinary = { mode, pool };
    try { extraordinaryAuthorize(caster, spellId, extraordinary, extraordinaryReaders); }
    catch (error) { return { ok: false, reason: String((error as Error).message) }; }
    const choice = extraordinaryChoices(caster, mode, pool, extraordinaryReaders).find(spell => spell.id === spellId);
    if (!choice || choice.summonChoices.length && !choice.summonChoices.some(pick => pick.id === summonPick))
        return { ok: false, reason: 'Pick a spell and its summon choice.' };
    const bridge = (window as unknown as { ftTryCommitAbilityUse?: (input: ExtraordinaryTargets & {
        encounterId: string; actorId: string; abilityId: string; extraordinary: ExtraordinaryCast;
        castLevel: string; rollPrivate: number; rawDiceMod: number; rawDiceAdvDisStacks: number;
    }) => Promise<'handled' | 'refused' | null> }).ftTryCommitAbilityUse;
    if (!bridge) { refuseUnported('sanguine-extraordinary:browser-resolution-unavailable'); return { ok: false }; }
    const result = await bridge({ encounterId, actorId: casterId, abilityId: spellId, ...targets, extraordinary,
        castLevel: String(choice.level), rollPrivate: Number(sc.roll_private ?? 0),
        rawDiceMod: Number(sc.raw_dice_mod ?? 0), rawDiceAdvDisStacks: Number(sc.raw_dice_adv_dis_stacks ?? 0),
        ...(targets.summon ? { summon: { ...targets.summon, ...(summonPick ? { pick: summonPick } : {}) } } : {}) });
    if (result === null) refuseUnported('sanguine-extraordinary:browser-resolution-unavailable');
    return { ok: result === 'handled', reason: result === 'handled' ? undefined : 'The spell was not cast; see the action warning.' };
}

/** The catalog gate needs a stored PC; practice casts still act through their held local creature. */
export function sanguineCatalogCasterId(casterId: string, caster: Record<string, unknown>): string {
    return ftCurrentTruthSink() ? String(itemField(record(caster.player_characters), 'item_id') ?? '') : casterId;
}

async function openExtraordinaryChoice(sc: SidelogContext, mode: ExtraordinaryMode): Promise<void> {
    const casterId = String(sc.caster_id ?? '');
    const { body, footer } = openCard(mode === 'adlib' ? 'Ad-lib — choose a spell' : 'Voidgrasp — choose a spell');
    bodyMessage(body, 'Loading spell choices…');
    const token = openToken;
    const where = await ensureActorRowHeld(casterId, String(sc.encounter_id ?? ''), { what: 'Extraordinary spell' });
    if (openToken !== token) return;
    if (where.kind !== 'encounter') { bodyMessage(body, 'Select this caster in an encounter to cast.', 'text-warning'); return; }
    sc = { ...sc, encounter_id: where.encounterId };
    try {
        const response = await fetch(baseUrl() + 'options/api/sanguine_extraordinary_spells_inc.php', {
            method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ caster_id: sanguineCatalogCasterId(casterId, where.row), mode }) });
        if (!response.ok) throw new Error('Could not load spell choices.');
        const data = await response.json() as { schema: string; inputs: PrepInputs };
        if (data.schema !== 'janus.sanguine.extraordinary.inputs.v1') throw new Error('Spell choices need the current browser bundle.');
        if (openToken !== token) return;
        const caster = casterRowFor(casterId, where.encounterId) ?? where.row;
        const loader = (window as unknown as { fetchAndCacheObject?: (id: string) => Promise<unknown> }).fetchAndCacheObject;
        await Promise.all(statusRows(caster, 'target').map(statusItemId).filter(id => !extraordinaryReaders.definition(id))
            .map(id => loader ? loader(id) : Promise.reject(new Error('The status definition loader is unavailable.'))));
        if (openToken !== token || !cardEl) return;
        const pool = extraordinaryPool(caster, data.inputs, extraordinaryReaders);
        const definitions = data.inputs.base.catalog;
        const read: ExtraordinaryReaders = { ...extraordinaryReaders, definition: id => extraordinaryReaders.definition(id) ?? definitions[id] ?? null };
        const spells = extraordinaryChoices(caster, mode, pool, read);
        if (!spells.length) { bodyMessage(body, 'This caster has no eligible spell for that Sanguine.', 'text-warning'); return; }
        body.innerHTML = '';
        const hint = document.createElement('p');
        hint.className = 'small text-muted';
        hint.textContent = `Costs ${extraordinaryQuote(caster, mode, read).cost} Sanguine; pays the spell’s normal mana. Choose targets after choosing the spell.`;
        const { wrap, select } = labelledSelect('Spell', 'Choose a spell');
        addOption(select, '', 'Choose a spell…');
        for (const spell of spells) addOption(select, spell.id, `Level ${spell.level} — ${spell.name}`);
        const picked = trackedValue(select);
        const { wrap: summonWrap, select: summonSelect } = labelledSelect('Summon', 'Choose a creature');
        summonSelect.classList.remove('ts-select');
        const summonValue = trackedValue(summonSelect);
        summonWrap.hidden = true;
        select.addEventListener('change', () => {
            const spell = spells.find(spell => spell.id === picked());
            (window as unknown as { destroyTomSelectIn?: (root: unknown) => void }).destroyTomSelectIn?.(summonWrap);
            summonSelect.innerHTML = '';
            summonWrap.hidden = !spell?.summonChoices.length;
            for (const choice of spell?.summonChoices ?? []) addOption(summonSelect, choice.id, choice.name);
            summonSelect.classList.toggle('ts-select', !summonWrap.hidden);
            if (!summonWrap.hidden) hydrateCardSelects();
        });
        body.append(hint, wrap, summonWrap);
        hydrateCardSelects();
        addFooterButtons(footer, 'Choose targets / Cast', async () => {
            const spell = spells.find(spell => spell.id === picked());
            if (!spell) return { ok: false, reason: 'Pick a spell to cast.' };
            const summonPick = summonValue();
            if (spell.summonChoices.length && !spell.summonChoices.some(pick => pick.id === summonPick))
                return { ok: false, reason: 'Choose a creature to summon.' };
            const loader = (window as unknown as { fetchAndCacheObject?: (id: string) => Promise<unknown> }).fetchAndCacheObject;
            if (!loader) return { ok: false, reason: 'The spell definition loader is unavailable.' };
            // Native action definitions and waiver carriers must be held before targeting or drawing.
            await Promise.all([...new Set([spell.id, SANGUINE_POOL_ID, ...statusRows(caster, 'target').map(statusItemId)])].map(id => loader(id)));
            if (openToken !== token || !cardEl) return { ok: false, reason: 'The spell choice was cancelled.' };
            const definition = extraordinaryReaders.definition(spell.id);
            if (!definition) return { ok: false, reason: 'The spell definition is unavailable.' };
            extraordinaryAuthorize(casterRowFor(casterId, where.encounterId) ?? caster, spell.id, { mode, pool }, extraordinaryReaders);
            const arm = (window as unknown as { ftActionBarExtraordinarySpell?: (id: string, definition: Record<string, unknown>, summon: string,
                commit: (targets: ExtraordinaryTargets) => void) => Promise<boolean> }).ftActionBarExtraordinarySpell;
            if (!arm) return { ok: false, reason: 'The spell targeting controls are unavailable.' };
            let dispatched = false;
            const opened = await arm(casterId, definition, summonPick, targets => {
                if (dispatched) return;
                dispatched = true;
                void castExtraordinaryChoice(sc, mode, pool, spell.id, summonPick, targets).then(result => {
                    if (!result.ok && result.reason) warn(result.reason);
                }).catch(error => warn(String((error as Error).message))).finally(() => {
                    (window as unknown as { cancelTargetDraft?: (reason: string) => void }).cancelTargetDraft?.('extraordinary-cast-complete');
                });
            });
            return { ok: opened, reason: 'Could not open the spell’s targeting controls.' };
        });
    } catch (error) { if (openToken === token) bodyMessage(body, String((error as Error).message), 'text-warning'); }
}

// ── Contingency ─────────────────────────────────────────────────────────────────


async function openContingency(sc: SidelogContext, armingItemId: string): Promise<void> {
    const casterId = String(sc.caster_id ?? '');
    const { body, footer } = openCard('Contingency — arm a delayed spell');
    bodyMessage(body, 'Loading storable spells…');

    const token = openToken;
    const where = await ensureActorRowHeld(casterId, String(sc.encounter_id ?? ''), { what: 'Contingency' });
    if (openToken !== token) return;
    if (where.kind !== 'encounter') { bodyMessage(body, 'Contingency can only be armed while in an encounter.', 'text-warning'); return; }
    const encounterId = where.encounterId;
    const held = casterRowFor(casterId, encounterId) ?? where.row;
    const fetchDefinition = (window as unknown as { fetchAndCacheObject?: (id: string) => Promise<unknown> }).fetchAndCacheObject;
    const needed = [...contingencyKnownSpellIds(held, gateReaders), armingItemId, SANGUINE_POOL_ID,
        ...statusRows(held, 'target').map(statusItemId)].filter(isUuid);
    try {
        await Promise.all([...new Set(needed)].filter(id => !gateReaders.definition?.(id)).map(async id => {
            if (!fetchDefinition) throw new Error('Definition loader unavailable');
            await fetchDefinition(id);
        }));
    } catch { bodyMessage(body, 'Could not load storable spells. Close and try again.', 'text-danger'); return; }
    if (openToken !== token) return;
    const read = await contingencyReaders(encounterId, String(sc.campaign_id ?? ''));
    if (openToken !== token) return;
    const data = { spells: contingencyEligibleSpells({ caster: held, encounterId, armingItemId, read }).map(a => ({
        id: String(itemField(a.spell, 'item_id')), name: String(itemField(a.spell, 'item_name')), level: a.spellLevel, max_cast_level: a.maxCastLevel })),
        conditions: CONTINGENCY_CONDITIONS.map(value => ({ value, label: value.replace('vstatus:', '').replace(/_/g, ' ') })),
        max_spell_level: 5,
        arming_mana: armingItemId === SPELL_CONTINGENCY_ID ? Number(read.value(read.definition(armingItemId), 'spell_level') ?? 6) : 0,
        charge_cost: contingencyArmingCost(held, armingItemId, read).cost };
    const spells = Array.isArray(data.spells) ? data.spells : [];
    const conditions = Array.isArray(data.conditions) ? data.conditions : [];
    if (spells.length === 0) {
        bodyMessage(body, 'You have no spell that can be stored in a Contingency (need a known Self-range spell, level 1–5).', 'text-warning');
        return;
    }

    body.innerHTML = '';
    const maxLevel = Number(data.max_spell_level ?? 5) || 5;
    const hint = document.createElement('p');
    hint.className = 'small text-muted';
    // Quote whichever door this is: the sanguine spends charges, the spell spends its own mana. Both
    // reserve the stored spell's mana on top of that.
    const armingMana = Number(data.arming_mana ?? 0);
    const armingCost = armingMana > 0
        ? `${armingMana} mana to cast Spell Contingency`
        : `${Number(data.charge_cost ?? 0)} Sanguine`;
    hint.textContent = `Costs ${armingCost} and reserves the mana for the level you choose (up to level ${maxLevel}). When the trigger fires, the spell casts on you automatically.`;
    const { wrap: spellWrap, select: spellSelect } = labelledSelect('Stored spell', 'Type to search your spells…');
    for (const s of spells) addOption(spellSelect, s.id, `${s.name} (L${s.level})`);

    // CAST LEVEL (owner 2026-07-24) — a plain native <select>, deliberately NOT a ts-select: it is
    // rebuilt every time the stored spell changes, and Tom Select would need a re-sync per rebuild for
    // a list of at most five entries that nobody needs to type-search. Its range is the chosen spell's
    // own level up to the browser-authorized ceiling (its own level when it cannot upcast).
    const levelWrap = document.createElement('div');
    levelWrap.className = 'mb-3';
    levelWrap.hidden = true; // nothing to choose until a spell is picked
    const levelLabel = document.createElement('label');
    levelLabel.className = 'form-label small fw-semibold';
    levelLabel.textContent = 'Cast at level';
    const levelSelect = document.createElement('select');
    levelSelect.className = 'form-select form-select-sm';
    const levelHint = document.createElement('div');
    levelHint.className = 'form-text small';
    levelWrap.append(levelLabel, levelSelect, levelHint);

    const { wrap: condWrap, select: condSelect } = labelledSelect('Trigger', 'Type to search triggers…');
    for (const c of conditions) addOption(condSelect, c.value, c.label);
    body.append(hint, spellWrap, levelWrap, condWrap);
    const spellValue = trackedValue(spellSelect); // registered BEFORE syncLevels' listener so its
    const conditionValue = trackedValue(condSelect); // tracked value is already fresh when we read it

    const spellById = new Map<string, SpellRow>();
    for (const s of spells) spellById.set(s.id, s);

    function updateLevelHint(): void {
        const lv = Number(levelSelect.value || 0);
        if (lv <= 0) { levelHint.textContent = ''; return; }
        levelHint.textContent = levelSelect.disabled
            ? `Reserves ${lv} mana now — this spell cannot be cast at a higher level.`
            : `Reserves ${lv} mana now.`;
    }

    function syncLevels(): void {
        const spell = spellById.get(spellValue());
        levelSelect.innerHTML = '';
        if (!spell) {
            levelWrap.hidden = true;
            return;
        }
        const base = Math.max(1, Number(spell.level) || 1);
        const ceiling = Math.max(base, Math.min(maxLevel, Number(spell.max_cast_level ?? base) || base));
        for (let lv = base; lv <= ceiling; lv += 1) {
            addOption(levelSelect, String(lv), lv === base ? `Level ${lv} (its own level)` : `Level ${lv} (upcast)`);
        }
        levelSelect.value = String(base);
        levelSelect.disabled = ceiling === base;
        levelWrap.hidden = false;
        updateLevelHint();
    }

    spellSelect.addEventListener('change', syncLevels);
    levelSelect.addEventListener('change', updateLevelHint);
    hydrateCardSelects();
    syncLevels(); // honor a pre-selected spell (Tom Select can adopt the first option)

    addFooterButtons(footer, 'Arm Contingency', async (): Promise<ActionResult> => {
        const spellId = spellValue();
        const condition = conditionValue();
        if (!isUuid(spellId)) return { ok: false, reason: 'Pick a spell to store.' };
        if (!condition) return { ok: false, reason: 'Pick a trigger.' };
        const castLevel = Number(levelSelect.value || 0);
        const openSideLog = (window as unknown as { openSideLog?: () => void }).openSideLog;
        // The browser gate, then the client-truth commit (sanguine_truth_commit_inc.php). The shared actor-row loader gets the caster's
        // row (held, or its encounter fetched into the page); the legacy sanguine_contingency_arm_inc.php is gone (N6 2026-10-01, R-074).
        // Contingency arms only inside an encounter (ft_contingency_arm's own rule): an encounter-less sheet is refused with its words.
        const where = await ensureActorRowHeld(casterId, String(sc.encounter_id ?? ''), { what: 'Contingency' });
        if (where.kind !== 'encounter') {
            return { ok: false, reason: where.kind === 'refused' && where.reason === 'row-only-unsupported'
                ? 'Contingency can only be armed while in an encounter.' : where.kind === 'refused' ? where.message : 'Contingency can only be armed while in an encounter.' };
        }
        const encounterId = where.encounterId;
        const row = casterRowFor(casterId, encounterId) ?? where.row;
        const picked = spellById.get(spellId);
        if (!picked) return { ok: false, reason: 'Pick a spell to store.' };
        const level = castLevel > 0 ? castLevel : Math.max(1, Number(picked.level) || 1);
        const verdict = planContingencyArm({ caster: row, encounterId, armingItemId, spellId, condition, castLevel: level,
            read: await contingencyReaders(encounterId, String(sc.campaign_id ?? '')) });
        if (verdict.kind === 'refuse') return { ok: false, reason: verdict.message };
        if (typeof openSideLog === 'function') openSideLog();
        return armThroughCommit({ casterId, encounterId, spellId, condition, castLevel: level, armingItemId, decision: verdict });
    });
}

/** POST one Contingency arm to the client-truth commit; a lost answer retries the SAME operation id (the commit is idempotent). */
export async function armThroughCommit(i: { casterId: string; encounterId: string; spellId: string; condition: string; castLevel: number; armingItemId: string;
    decision: PlannedContingency }): Promise<ActionResult> {
    const body = { schema: SANGUINE_SCHEMA, kind: 'contingency_arm', operation_id: crypto.randomUUID().toUpperCase(), encounter_id: i.encounterId,
        caster_id: i.casterId, spell_id: i.spellId, condition: i.condition, cast_level: i.castLevel, arming_item_id: i.armingItemId, before: i.decision.before, plan: i.decision.plan };
    try {
        const encounter = record(window.FateTide?.encounterCache?.get(i.encounterId));
        if (!encounter) throw new Error('sanguine:encounter-not-held');
        const p = i.decision.plan;
        const verdict: GmBoundaryVerdict = { ...p, statusStacks: [], playerCharactersChanged: [],
            charges: p.charges.map(c => ({ ...c, charges: c.after })) };
        const applied = diceAppliedFrame(body.operation_id, i.encounterId, i.casterId, verdict, encounter,
            record(window.FateTide?.campaignCache?.get(String(encounterField(encounter, 'encounter_master_parent_id') ?? '').toUpperCase())), gateReaders.definition!);
        const { status, result } = await saveDiceTruth(baseUrl() + 'options/game/actions/sanguine_truth_commit_inc.php', body);
        window.dispatchEvent(new CustomEvent('ft:sanguine-truth-answer', { detail: { body, status, json: result } }));
        if (status === 200 && result.ok === true) {
            publishSavedDice(body, result, applied);
            return { ok: true };
        }
        if (status === 409) {
            reconcileCaster(i.casterId, i.encounterId, record(result.values) ?? undefined);
            return { ok: false, reason: 'Your Sanguine, mana, or held Contingency changed elsewhere — nothing was spent. Try again with the current state.' };
        }
        return { ok: false, reason: String(result.message || (status === 403 ? 'You do not control this caster — Contingency refused.' : `Contingency refused (${String(result.error ?? status)}).`)) };
    } catch { return { ok: false, reason: 'Contingency could not be confirmed — check your statuses before arming again.' }; }
}

// ── shared helpers ──────────────────────────────────────────────────────────────

function isUuid(s: string): boolean {
    const fn = (window as unknown as { checkIfValidUUID?: (v: string) => boolean }).checkIfValidUUID;
    return typeof fn === 'function' ? !!fn(s) : /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
}


interface ActionResult { ok: boolean; reason?: string }


function addFooterButtons(footer: HTMLDivElement, confirmText: string, onConfirm: () => Promise<ActionResult>): void {
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'btn btn-sm btn-outline-secondary';
    cancel.textContent = 'Cancel';
    cancel.addEventListener('click', closeCard);

    const confirm = document.createElement('button');
    confirm.type = 'button';
    confirm.className = 'btn btn-sm btn-danger';
    confirm.textContent = confirmText;
    // The card this button belongs to; a later open bumps openToken, so a stale resolution won't touch it.
    const myToken = openToken;
    confirm.addEventListener('click', async () => {
        if (confirm.disabled) return;
        confirm.disabled = true;
        cancel.disabled = true;
        showCardStatus('');
        let result: ActionResult;
        try {
            result = await onConfirm();
        } catch {
            result = { ok: false, reason: 'Request failed — try again.' };
        }
        if (openToken !== myToken) return; // card was closed/replaced while the request was in flight
        if (result.ok) {
            closeCard();
        } else {
            showCardStatus(result.reason ?? 'The action was refused.'); // textContent — safe
            confirm.disabled = false;
            cancel.disabled = false;
        }
    });

    footer.append(cancel, confirm);
}

/**
 * Entry point (window-attached) fired by the action-bar button for the dedicated-cast sanguines AND for
 * the level-6 Spell Contingency, which shares the contingency dialog through its own arming door.
 */
export function ftSanguineDedicatedCast(abilityId: string): void {
    const mode = dedicatedSanguineMode(abilityId);
    if (mode === '') return;
    const sc = readSidelogContext();
    if (!sc || !isUuid(String(sc.caster_id ?? ''))) {
        warn('Select a creature you control before casting this.');
        return;
    }

    if (mode === 'contingency') void openContingency(sc, contingencyArmingItemId(abilityId));
    else void openExtraordinaryChoice(sc, mode);
}

(window as unknown as { ftSanguineDedicatedCast?: typeof ftSanguineDedicatedCast }).ftSanguineDedicatedCast = ftSanguineDedicatedCast;

export {};
